import { LEVEL_UI, STATUS_ESTRUTURA_UI } from '@/ui';
import { calcPrazoBaixa, flowStepsProgress, nivelEfetivoVisivel, prazoHorasPorNivel } from '@/domain';
import { agruparAutoriaPorTarp, autoriaParaExibicao, AUTOR_NAO_RESOLVIDO } from '@/domain/tarp/autoria';
import type { AutoriaTarp } from '@/services/datasources';
import type { StatusEstrutura } from '@/domain/estrutura';
import type { TarpRecord } from '@/services/datasources';
import { tarpCodeLabel, tarpDisplayId } from '../catalog-stub';
import { TarpStatusPill } from './TarpStatusPill';

/** Rótulo do prazo da baixa (⏳ restantes / ⏰ estourado), contado do encaminhamento. */
function prazoLabel(t: TarpRecord): { txt: string; estourado: boolean } | null {
  // GUPM-207: prazo pelo nível EFETIVO (uma TARP que escalou p/ emergência tem 12h, não 24h).
  // `Visivel` = rascunho EM_ABERTURA não escala (fix #118); só pendentes de baixa chegam aqui.
  const p = calcPrazoBaixa(t.concludedAt, nivelEfetivoVisivel(t), new Date().toISOString());
  if (!p) return null;
  return p.estourado
    ? { txt: `⏰ prazo estourado (${Math.abs(Math.round(p.restanteH))}h atrás)`, estourado: true }
    : { txt: `⏳ ${Math.round(p.restanteH)}h restantes`, estourado: false };
}

/**
 * Pill da barragem. Reflete a COR DO STATUS ATUAL da estrutura (mesma fonte dos
 * chips do header — `STATUS_ESTRUTURA_UI`). Sem status conhecido, cai numa pill
 * neutra (navy da marca).
 */
function DamPill({ code, status }: { code: string; status?: StatusEstrutura }) {
  if (status) {
    const s = STATUS_ESTRUTURA_UI[status];
    return (
      <span
        className="rounded-full border px-2 py-0.5 text-xs font-bold"
        style={{ color: s.color, backgroundColor: s.bg, borderColor: s.border }}
        title={`Barragem ${code}: ${s.label}`}
      >
        Barragem {code}
      </span>
    );
  }
  return (
    <span className="rounded-full border border-jf-border bg-jf-surface2 px-2 py-0.5 text-xs font-bold text-slate-200">
      Barragem {code}
    </span>
  );
}

/** Barra de progresso do checklist (passos concluídos). */
function Stepper({ done, total, color }: { done: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="mt-2.5">
      <div className="mb-1 flex justify-between text-xs">
        <span className="text-jf-muted">Progresso do passo a passo</span>
        <span className="font-bold" style={{ color }}>
          {done}/{total} passos ({pct}%)
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-jf-border">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/**
 * Lista de TARPs em aberto (dados reais via datasource). As pendentes de baixa
 * (`CONCLUIDA_AGUARDANDO`) ganham o card enriquecido fiel ao V8: identificador
 * sequencial (TR-nnnn-barragem-tipo), pill da barragem por status, instrumento,
 * prazo dinâmico, origem/nível, data de abertura, autor, responsável, stepper,
 * observação (se houver) e CTA "Dar Baixa".
 */
export function TarpList({
  tarps,
  onSelect,
  onDarBaixa,
  damStatus,
  podeDarBaixa = false,
  autoria,
}: {
  tarps: TarpRecord[];
  /** Clique no CORPO do card → revisão read-only (Tela A). */
  onSelect: (t: TarpRecord) => void;
  /** Clique no botão "Dar Baixa" do card → atalho direto pro formulário de baixa
   *  (Tela B), pulando a revisão. Sem handler, o botão cai no `onSelect`. */
  onDarBaixa?: (t: TarpRecord) => void;
  /** Status consolidado por código de barragem (do useDamScope) — colore a pill. */
  damStatus?: Record<string, StatusEstrutura>;
  /**
   * GUPM-290: o usuário pode dar baixa (RT/admin)? Decide o RÓTULO do atalho, não
   * o acesso — o operador segue chegando à TARP, só por "Ver TARP" (Tela A), que é
   * o que o clique dele já fazia. Antes o botão era gateado só por status e
   * prometia ao operador uma ação que `canClose()` e a RLS negam.
   *
   * Booleano pronto de propósito: quem resolve o perfil é o orquestrador, via
   * `canClose()` do domínio — este componente não consulta identidade
   * (`architecture.md`: `components/` sem `if` de negócio). Default `false`:
   * chamador que esquecer de passar mostra a opção mais restritiva.
   */
  podeDarBaixa?: boolean;
  /**
   * GUPM-292: atribuições de autoria de TODAS as TARPs da lista (uma consulta em
   * lote). Ausente/vazio = autoria ainda não carregada, ou consulta falhou: o
   * card renderiza sem os rótulos, nunca com rótulo vazio.
   */
  autoria?: readonly AutoriaTarp[];
}) {
  if (tarps.length === 0) {
    return <p className="py-6 text-center text-sm text-jf-muted">Nenhum TARP em aberto.</p>;
  }
  // Ordena por urgência: pendentes de baixa (com prazo) primeiro, do menor tempo
  // restante (estourados no topo); rascunhos/abertas depois, mais recentes antes.
  const now = new Date().toISOString();
  // GUPM-292: indexa a autoria uma vez, não uma varredura por card.
  const autoriaPorTarp = agruparAutoriaPorTarp(autoria ?? []);
  const ordered = [...tarps].sort((a, b) => {
    const pa = calcPrazoBaixa(a.concludedAt, nivelEfetivoVisivel(a), now);
    const pb = calcPrazoBaixa(b.concludedAt, nivelEfetivoVisivel(b), now);
    if (pa && !pb) return -1;
    if (!pa && pb) return 1;
    if (pa && pb) return pa.restanteH - pb.restanteH;
    return new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime();
  });
  return (
    <div className="space-y-2">
      {ordered.map((t) => {
        // GUPM-207: nível EFETIVO (base do catálogo, ou EMERGÊNCIA se o fluxo escalou
        // ao paebm) — colore o card, define o prazo da baixa e a ordenação por urgência.
        // `Visivel`: um rascunho EM_ABERTURA NÃO escala (fix #118) — o autosave grava o
        // caminho antes do "Registrar", então não pode pintar emergência falsa na lista.
        const nivel = nivelEfetivoVisivel(t);
        const u = LEVEL_UI[nivel];
        const id = tarpDisplayId(t.seq, t.damCode, t.tarpCode, t.instrumentCode);
        const pendenteBaixa = t.status === 'CONCLUIDA_AGUARDANDO';
        const prazo = pendenteBaixa ? prazoLabel(t) : null;
        // GUPM-190 T6: steps_total chega null do banco nos TARPs em aberto → deriva
        // do protocolo (tamanho do fluxo); done capado a [0,total] no domínio.
        const { done: stepsDone, total: stepsTotal } = flowStepsProgress(
          t.tarpCode,
          t.stepsCompleted,
          t.stepsTotal,
        );
        // `iniciou` vem do REGISTRO (`openedByName`, já carregado com a lista), não
        // da consulta de autoria: perder autoria que já temos porque uma segunda
        // consulta atrasou ou falhou seria regressão. A consulta só ACRESCENTA
        // editou/abriu/baixa — por isso o `iniciou` que ela devolve é descartado.
        const autoriaDoCard = autoriaParaExibicao([
          {
            tarpId: t.id,
            momento: 'iniciou' as const,
            actorName: t.openedByName ?? AUTOR_NAO_RESOLVIDO,
            ts: t.openedAt,
          },
          ...(autoriaPorTarp[t.id] ?? []).filter((a) => a.momento !== 'iniciou'),
        ]);
        return (
          <div
            key={t.id}
            className="w-full overflow-hidden rounded-xl border"
            style={{ borderColor: u.border, background: u.bg }}
          >
            {/* Corpo do card → revisão read-only (Tela A). */}
            <button
              type="button"
              onClick={() => onSelect(t)}
              className={`block w-full px-3.5 pt-3.5 text-left transition hover:bg-white/[0.04] ${
                pendenteBaixa ? 'pb-2.5' : 'pb-3.5'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-white" title={id.full}>
                    {id.num}
                  </span>
                  {t.damCode ? (
                    <DamPill code={t.damCode} status={damStatus?.[t.damCode]} />
                  ) : (
                    <span className="rounded-md border border-jf-border/60 bg-jf-border/20 px-1.5 py-0.5 text-[11px] font-semibold text-jf-muted">
                      Ainda não identificada
                    </span>
                  )}
                  {t.instrumentCode && (
                    <span className="font-mono text-sm text-white">{t.instrumentCode}</span>
                  )}
                  {prazo && (
                    <span
                      className={`text-sm font-bold ${prazo.estourado ? 'text-level-emergencia' : ''}`}
                      style={prazo.estourado ? undefined : { color: u.color }}
                    >
                      {prazo.txt}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <TarpStatusPill status={t.status} />
                  <span aria-hidden className="text-base leading-none text-jf-muted">›</span>
                </div>
              </div>

              {/* Título do protocolo — destacado e consistente em todos os cards. */}
              <div className="mt-1 text-sm font-semibold text-white">
                <span aria-hidden className="mr-1">{u.icon}</span>
                {tarpCodeLabel(t.tarpCode, t.instrumentType)}
              </div>

              {pendenteBaixa ? (
                <div className="mt-0.5 space-y-0.5 text-xs text-jf-muted">
                  <div>Prazo para baixa: {prazoHorasPorNivel(nivel)}h ({u.label})</div>
                  <div>Data de abertura: {new Date(t.openedAt).toLocaleString('pt-BR')}</div>
                  <div>Responsável: RT Foscentro</div>
                </div>
              ) : (
                <div className="mt-0.5 text-xs text-jf-muted">
                  {t.status === 'EM_ABERTURA' ? 'rascunho iniciado ' : 'aberto '}
                  {new Date(t.openedAt).toLocaleString('pt-BR')}
                </div>
              )}

              {/* GUPM-292: autoria por ATO. Substitui o antigo "Aberta por:
                  {openedByName}", que mostrava `opened_by` — gravado na CRIAÇÃO do
                  rascunho e nunca alterado pelo `confirmAbertura` — e portanto
                  creditava a abertura a quem só tinha iniciado. Cada momento sai
                  só se existir: o card não anuncia abertura que não houve. */}
              {autoriaDoCard.length > 0 && (
                <div className="mt-1 space-y-0.5 text-xs text-jf-muted">
                  {autoriaDoCard.map((a) => (
                    <div key={a.momento}>
                      {a.label}: {a.nomes}
                    </div>
                  ))}
                </div>
              )}

              {stepsTotal > 0 ? (
                <Stepper done={stepsDone} total={stepsTotal} color={u.color} />
              ) : null}

              {t.observation && (
                <p className="mt-2 text-xs italic text-jf-muted">Obs.: {t.observation}</p>
              )}
            </button>

            {/* Atalho explícito. Para RT/admin pula a revisão e abre direto o
                formulário de baixa (Tela B); para operador é "Ver TARP" e abre a
                Tela A read-only (GUPM-290). Botão próprio (não aninhado no clique
                do corpo); aria-label distinto por intenção e do botão do rodapé
                do modal. */}
            {pendenteBaixa && (
              <div className="flex justify-end px-3.5 pb-3.5">
                <button
                  type="button"
                  onClick={() => (podeDarBaixa ? (onDarBaixa ?? onSelect) : onSelect)(t)}
                  aria-label={
                    podeDarBaixa ? `Dar baixa na TARP ${id.num}` : `Ver TARP ${id.num}`
                  }
                  className="rounded-lg px-3 py-1.5 text-xs font-bold text-black transition hover:brightness-90"
                  style={{ background: u.color }}
                >
                  {podeDarBaixa ? '📝 Dar Baixa' : 'Ver TARP'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
